import { html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { booleanConverter } from './common/converters';
import { TeComponent } from './common/te-component';
import { theme } from './common/theme';
import './te-icon';
import './te-text';
import './te-spacing';
import { styleMap } from 'lit/directives/style-map';

/** Checkbox Component */
@customElement('te-checkbox')
export class TeCheckbox extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-checkbox-wrapper {
        display: flex;
        position: relative;
        width: fit-content;
        align-items: center;
        justify-content: center;
        gap: 16px;
      }

      .te-checkbox-wrapper.reverse {
        flex-flow: row-reverse;
      }

      .te-checkbox-input {
        border: 1px solid ${theme.colors.shade3};
        background: ${theme.colors.shade0};
        border-radius: 4px;
        position: relative;
      }

      .checked .te-checkbox-input {
        background: ${theme.colors.primary.v0};
        border: none;
      }

      .te-checkbox {
        position: absolute;
        margin: 0;
        padding: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
      }

      .disabled .te-checkbox {
        cursor: default;
      }

      .te-checkbox-checked {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        border-radius: 4px;
        pointer-events: none;
      }

      .disabled .te-checkbox-input {
        background: ${theme.colors.shade3};
      }

      .te-checkbox-label {
        display: flex;
      }
    `,
  ];
  /** Checkbox is checked */
  @property({ type: Boolean, converter: booleanConverter }) checked = false;
  /** Checkbox is partially checked */
  @property({ type: Boolean, converter: booleanConverter }) indeterminate?: boolean;
  /** Checkbox is disabled */
  @property({ type: Boolean, converter: booleanConverter }) disabled = false;
  /** Placement of the checkbox label */
  @property({ type: String }) labelPlacement: 'left' | 'right' = 'left';
  /** Text value for label */
  @property({ type: String }) label = '';

  handleCheckboxChange(e: Event) {
    if (this.disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    this.handleChange(e);
    if (this.indeterminate === undefined) {
      this.checked = (e.currentTarget as HTMLInputElement).checked;
    }
  }

  renderIcon() {
    if (!this.checked) return;
    return html`
      <div class="te-checkbox-checked">
        <te-icon
          size="12"
          name="${this.indeterminate ? 'indeterminate-checkmark' : 'checkmark'}"
          color=${theme.colors.shade0.toString()}
        ></te-icon>
      </div>
    `;
  }

  renderLabel() {
    if (!this.label) return;
    return html`
      <div class="te-checkbox-label">
        <te-text size="14">${this.label}</te-text>
      </div>
    `;
  }

  @state() wrapperSize = 0;

  getStyle() {
    return {
      width: '16px',
      height: '16px',
    };
  }

  getWrapperStyle() {
    return {
      width: `${this.wrapperSize ? this.wrapperSize : 'unset'}px`,
      height: `${this.wrapperSize ? this.wrapperSize : 'unset'}px`,
    };
  }

  render() {
    const classes = ['te-checkbox-wrapper'];
    if (this.checked) classes.push('checked');
    if (this.disabled) classes.push('disabled');
    if (this.labelPlacement === 'right') classes.push('reverse');
    return html`
      <div class="${classes.join(' ')}" style=${styleMap(this.getWrapperStyle())}>
        ${this.renderLabel()}
        <div class="te-checkbox-input" style=${styleMap(this.getStyle())}>
          ${this.renderIcon()}
        </div>
        <input
          id="${this.testId}"
          type="checkbox"
          class="te-checkbox"
          .checked=${this.checked}
          .disabled=${this.disabled}
          @change=${this.handleCheckboxChange}
        />
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-checkbox': TeCheckbox;
  }
}
